import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AlertTriangle, Paperclip } from "lucide-react";
import { fmtSize } from "./AssetPicker";
import { fieldLabel } from "./mergeFields";

// Server-rendered preview: the exact subject/body (resolved fields + sender's own signature) that will be sent.
export function ComposePreview({ payload }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const key = JSON.stringify(payload);
  useEffect(() => {
    let live = true;
    api.post("/comm-preview", JSON.parse(key)).then((r) => { if (live) setP(r.data); }).catch((e) => live && setErr(apiError(e)));
    return () => { live = false; };
  }, [key]);
  if (err) return <p className="p-4 text-sm text-rose-600">{err}</p>;
  if (!p) return <p className="p-4 text-sm text-slate-400">Building preview…</p>;
  return (
    <div data-testid="compose-preview">
      <div className="px-4 py-2.5 bg-slate-50 border-b text-sm space-y-0.5" data-testid="compose-preview-headers">
        <p className="text-slate-600"><span className="text-slate-400 w-16 inline-block">From</span>{p.from_name} {p.from_email ? `<${p.from_email}>` : "(no mailbox connected)"}</p>
        <p className="text-slate-600"><span className="text-slate-400 w-16 inline-block">To</span>{p.to_name ? `${p.to_name} ` : ""}{p.to_email ? `<${p.to_email}>` : "—"}</p>
        <p className="text-slate-900 font-medium" data-testid="compose-preview-subject"><span className="text-slate-400 w-16 inline-block font-normal">Subject</span>{p.subject || "(no subject)"}</p>
      </div>
      {p.unresolved?.length > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex gap-2" data-testid="compose-preview-unresolved">
          <AlertTriangle className="w-4 h-4 shrink-0" />No value for this recipient: {p.unresolved.map(fieldLabel).join(", ")}. Fill or remove before sending.
        </div>
      )}
      {p.assets?.attachments?.length > 0 && (
        <div className="px-4 py-2 border-b text-xs text-slate-600 flex flex-wrap gap-1.5" data-testid="compose-preview-attachments">
          {p.assets.attachments.map((a) => <span key={a.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${a.status === "ok" ? "" : "border-rose-200 bg-rose-50 text-rose-700"}`}><Paperclip className="w-3 h-3" />{a.name} · {fmtSize(a.size)}{a.status !== "ok" ? ` · ${a.message}` : ""}</span>)}
        </div>
      )}
      {p.assets?.issues?.length > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 space-y-1" data-testid="compose-preview-asset-issues">
          {p.assets.issues.map((m) => <p key={m} className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />{m}</p>)}
        </div>
      )}
      {!p.has_signature && <p className="px-4 py-2 text-xs text-slate-500 border-b" data-testid="compose-preview-nosig">You have no saved signature. Add one in My Profile → Signature.</p>}
      <div className="px-4 py-3 text-sm [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-brand [&_a]:underline [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:font-semibold"
        data-testid="compose-preview-body" dangerouslySetInnerHTML={{ __html: p.bodyhtml || "<em>No content</em>" }} />
    </div>
  );
}
